import { useMemo } from "react";
import type { Service, ServiceCategory } from "@/gql/graphql";
import { getUserFriendlyErrorMessage } from "@/lib/error/error-utils";
import type {
  CategorySortUpdate,
  ServiceRowData,
  ServiceSortUpdate,
  ServicesFilters,
} from "../types";
import {
  useArchiveService,
  useArchiveServiceCategory,
  useCreateService,
  useCreateServiceCategory,
  useUpdateCategorySortOrder,
  useUpdateService,
  useUpdateServiceCategory,
  useUpdateServiceSortOrder,
} from "./use-services-mutation";
import { useServicesQuery } from "./use-services-query";

interface UseServicesProps {
  salonId?: string;
  filters?: ServicesFilters;
}

interface UseServicesReturn {
  // Data
  categories: ServiceCategory[];
  services: Service[];
  servicesByCategory: Record<string, ServiceRowData[]>;
  filteredCategories: ServiceCategory[];
  filteredServicesByCategory: Record<string, ServiceRowData[]>;

  // State
  loading: boolean;
  error: any;
  errorMessage: string | null;

  // Statistics
  totalServices: number;
  activeServices: number;
  totalCategories: number;
  activeCategories: number;

  // Actions
  retry: () => void;
  refetch: () => Promise<any>;

  // Category mutations
  createCategory: (input: any) => Promise<any>;
  updateCategory: (categoryId: string, input: any) => Promise<any>;
  deleteCategory: (categoryId: string) => Promise<any>;
  updateCategorySortOrder: (updates: CategorySortUpdate[]) => Promise<any>;

  // Service mutations
  updateServiceSortOrder: (updates: ServiceSortUpdate[]) => Promise<any>;
  createService: (input: any) => Promise<any>;
  updateService: (serviceId: string, input: any) => Promise<any>;
  archiveService: (serviceId: string) => Promise<any>;

  // Loading states
  isCreatingCategory: boolean;
  isUpdatingCategory: boolean;
  isArchivingCategory: boolean;
  isUpdatingSortOrder: boolean;
  isUpdatingServiceSortOrder: boolean;
  isCreatingService: boolean;
  isUpdatingService: boolean;
  isArchivingService: boolean;
}

/**
 * Main hook for services domain with combined query/mutation logic and filtering
 */
export const useServices = ({
  salonId,
  filters = {},
}: UseServicesProps = {}): UseServicesReturn => {
  // Query hook
  const { categories, services, servicesByCategory, loading, error, retry, refetch } =
    useServicesQuery({ salonId });

  // Mutation hooks
  const { execute: createCategoryAction, isLoading: isCreatingCategory } =
    useCreateServiceCategory();
  const { execute: updateCategoryAction, isLoading: isUpdatingCategory } =
    useUpdateServiceCategory();
  const { execute: archiveCategoryAction, isLoading: isArchivingCategory } =
    useArchiveServiceCategory();
  const { execute: updateSortOrderAction, isLoading: isUpdatingSortOrder } =
    useUpdateCategorySortOrder();

  const { execute: updateServiceSortOrderAction, isLoading: isUpdatingServiceSortOrder } =
    useUpdateServiceSortOrder();
  const { execute: createServiceAction, isLoading: isCreatingService } = useCreateService();
  const { execute: updateServiceAction, isLoading: isUpdatingService } = useUpdateService();
  const { execute: archiveServiceAction, isLoading: isArchivingService } = useArchiveService();

  // Sort categories by sortOrder (now only active categories from backend)
  const sortedCategories = useMemo(() => {
    return [...categories].sort((a, b) => a.sortOrder - b.sortOrder);
  }, [categories]);

  // Apply search filters to categories
  const filteredCategories = useMemo(() => {
    const { searchTerm } = filters;

    // Apply search filter if needed
    if (!searchTerm) return sortedCategories;

    return sortedCategories.filter((category) => {
      const categoryMatches = category.name.toLowerCase().includes(searchTerm.toLowerCase());

      const servicesMatch = servicesByCategory[category.id]?.some(
        (service) =>
          service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          service.serviceCode.toLowerCase().includes(searchTerm.toLowerCase())
      );

      return categoryMatches || servicesMatch;
    });
  }, [sortedCategories, servicesByCategory, filters.searchTerm, filters]);

  const filteredServicesByCategory = useMemo(() => {
    return filteredCategories.reduce(
      (acc, category) => {
        const categoryServices = servicesByCategory[category.id] || [];
        if (!filters.searchTerm) {
          acc[category.id] = categoryServices;
        } else {
          acc[category.id] = categoryServices.filter(
            (service) =>
              service.name.toLowerCase().includes(filters.searchTerm?.toLowerCase() ?? "") ||
              service.serviceCode.toLowerCase().includes(filters.searchTerm?.toLowerCase() ?? "")
          );
        }
        return acc;
      },
      {} as Record<string, ServiceRowData[]>
    );
  }, [filteredCategories, servicesByCategory, filters.searchTerm]);

  // Calculate statistics (now only active data since archived is handled separately)
  const totalServices = services.length;
  const activeServices = services.length; // All services from backend are active now
  const totalCategories = sortedCategories.length;
  const activeCategories = sortedCategories.length; // All categories from backend are active now

  // Transform error message
  const errorMessage = useMemo(() => {
    if (!error) return null;
    return getUserFriendlyErrorMessage(error);
  }, [error]);

  // Wrap mutation actions to include salonId
  const effectiveSalonId = salonId || ""; // Will be validated in mutations

  const createCategory = async (input: any) => {
    return createCategoryAction(effectiveSalonId, input);
  };

  const updateCategory = async (categoryId: string, input: any) => {
    return updateCategoryAction(effectiveSalonId, categoryId, input);
  };

  const deleteCategory = async (categoryId: string) => {
    return archiveCategoryAction(effectiveSalonId, categoryId);
  };

  const updateCategorySortOrder = async (updates: CategorySortUpdate[]) => {
    return updateSortOrderAction(effectiveSalonId, updates);
  };

  const updateServiceSortOrder = async (updates: ServiceSortUpdate[]) => {
    return updateServiceSortOrderAction(effectiveSalonId, updates);
  };

  const createService = async (input: any) => {
    return createServiceAction(effectiveSalonId, input);
  };

  const updateService = async (serviceId: string, input: any) => {
    return updateServiceAction(effectiveSalonId, serviceId, input);
  };

  const archiveService = async (serviceId: string) => {
    return archiveServiceAction(effectiveSalonId, serviceId);
  };

  return {
    // Data
    categories: sortedCategories,
    services,
    servicesByCategory,
    filteredCategories,
    filteredServicesByCategory,

    // State
    loading,
    error,
    errorMessage,

    // Statistics
    totalServices,
    activeServices,
    totalCategories,
    activeCategories,

    // Actions
    retry,
    refetch,

    // Category mutations
    createCategory,
    updateCategory,
    deleteCategory,
    updateCategorySortOrder,

    // Service mutations
    updateServiceSortOrder,
    createService,
    updateService,
    archiveService,

    // Loading states
    isCreatingCategory,
    isUpdatingCategory,
    isArchivingCategory,
    isUpdatingSortOrder,
    isUpdatingServiceSortOrder,
    isCreatingService,
    isUpdatingService,
    isArchivingService,
  };
};
